import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { pick } from "lodash";
import queryString from 'query-string';
import { useForm } from "@xvii/useform";
import config from 'cityads-v3/config'

import { getCoupons, getFilterCount } from "cityads-v3/actions/offers";
import { useEntities } from "cityads-v3/hooks/entities";
import { ensureArray } from "cityads-v3/utils/arrayUtils";
import { RegionTreeItem } from "cityads-v3/types/geos";
import { CategoryTreeItem } from "cityads-v3/types/categories";
import { RootState } from "cityads-v3/types/reducer";
import { useDebounceAction } from "cityads-v3/utils/useUtils";

/**
 * Возможные параметры в адресной строке
 */
type LocationParams = {
  category: number[],
  is_exclusive: boolean,
  is_personal: boolean,
  language_id: number[],
  sectoring: number[],
  offers: number[],
  region_id: number[],
  scopes: number[],
  status: number[],
  type: number[],

  page: number,
  limit: number,
  order: string,
}

/**
 * Контекст фильтров
 */
export const CouponFiltersContext = createContext(null);
export const useCouponFiltersContext = () => useContext<ReturnType<typeof useCouponFilters>>(CouponFiltersContext);

export function useCouponFilters() {
  const currentFilters = useFiltersFromLocation();
  const selectedFilters = useSelectedFilters(
    currentFilters
  )

  const clearFilters = useClearFilters(selectedFilters)

  const handleChangeOrder = useCallback((order: string) => {
    selectedFilters.fields.order.setValue(order);
    selectedFilters.handleSubmit()
  }, [selectedFilters])

  useLoadTotal(selectedFilters);

  return {
    clearFilters,
    selectedFilters,
    handleChangeOrder
  }
}

/**
 * Текущие значения в фильтрах
 * (в том числе до применения)
 */
function useSelectedFilters(
  currentFilters?: ReturnType<typeof useFiltersFromLocation>,
) {
  const setLocation = useSetLocation();

  const form = useForm({
    onSubmit: useCallback((formData) => {
      setLocation({
        category: formData.category,
        is_exclusive: formData.is_exclusive,
        is_personal: formData.is_personal,
        language_id: formData.language_id,
        offers: formData.offers,
        region_id: formData.region_id,
        scopes: formData.scopes,
        status: formData.status,
        type: formData.type,
        sectoring: formData.sectoring,

        page: 1,
        order: formData.order,
      })
    }, [setLocation]),
    fields: {
      category: { defaultValue: currentFilters?.category ?? [], valueType: [Number()] },
      is_exclusive: { defaultValue: currentFilters?.is_exclusive ?? false, valueType: Boolean() },
      is_personal: { defaultValue: currentFilters?.is_personal ?? false, valueType: Boolean() },
      language_id: { defaultValue: currentFilters?.language_id ?? [], valueType: [Number()] },
      sectoring: { defaultValue: currentFilters?.sectoring ?? [], valueType: [Number()] },
      offers: { defaultValue: currentFilters?.offers ?? [], valueType: [Number()] },
      region_id: { defaultValue: currentFilters?.region_id ?? [], valueType: [Number()] },
      scopes: { defaultValue: currentFilters?.scopes ?? [], valueType: [Number()] },
      status: { defaultValue: currentFilters?.status ?? [], valueType: [Number()] },
      type: { defaultValue: currentFilters?.type ?? [], valueType: [Number()] },

      order: { defaultValue: currentFilters?.order ?? '-id', valueType: String() },
    }
  })

  return form;
}

/**
 * Обработчик изменения адресной строки.
 * Первоисточником текущего состояния страницы
 * (фильтры, пагинация и т.д.) является адресная строка.
 * Все изменения нужно производить через неё.
 */
function useFiltersFromLocation() {
  const location = useLocation()
  const dispatch = useDispatch()
  const parseFilters = useParseLocation()
  const currentFilters = useMemo(() => parseFilters(location.search), [location.search, parseFilters])

  useEffect(() => {
    dispatch(getCoupons({
      filter: {
        ...pick(currentFilters, [
          'category',
          'is_exclusive',
          'is_personal',
          'language_id',
          'sectoring',
          'offers',
          'region_id',
          'scopes',
          'status',
          'type',
        ])
      },
      page: currentFilters.page,
      limit: currentFilters.limit,
      order: currentFilters.order,
    }))
  }, [currentFilters, dispatch])

  return currentFilters
}

/**
 * парсер параметров из текущего адреса страницы
 */
function useParseLocation() {
  return useCallback((search: Location['search']) => {
    const parsedSearch = queryString.parse(search, { arrayFormat: 'comma' }) as unknown as LocationParams;

    convertOldFilters(parsedSearch)

    Object.keys(parsedSearch).forEach((key: keyof LocationParams) => {
      if (!parsedSearch[key]) {
        delete parsedSearch[key];
      }
    })

    if (parsedSearch.category) { parsedSearch.category = ensureArray(parsedSearch.category) }
    if (parsedSearch.language_id) { parsedSearch.language_id = ensureArray(parsedSearch.language_id).map(Number) }
    if (parsedSearch.sectoring) { parsedSearch.sectoring = ensureArray(parsedSearch.sectoring) }
    if (parsedSearch.offers) { parsedSearch.offers = ensureArray(parsedSearch.offers).map(Number) }
    if (parsedSearch.region_id) { parsedSearch.region_id = ensureArray(parsedSearch.region_id) }
    if (parsedSearch.scopes) { parsedSearch.scopes = ensureArray(parsedSearch.scopes).map(Number) }
    if (parsedSearch.status) { parsedSearch.status = ensureArray(parsedSearch.status).map(Number) }
    if (parsedSearch.type) { parsedSearch.type = ensureArray(parsedSearch.type).map(Number) }

    return {
      ...parsedSearch,
      page: +parsedSearch.page || 1,
      limit: +parsedSearch.limit || config.PAGE_DEFAULT_LIMIT
    }
  }, [])
}

/**
 * конвертер старых фильтров
 */
function convertOldFilters(parsedSearch: LocationParams) {
  const oldFilters = (parsedSearch as LocationParams & { filter: any }).filter;
  if (oldFilters) {
    try {
      const filters = JSON.parse(oldFilters.join(','));
      if (!parsedSearch.category && filters.category_id) {
        parsedSearch.category = ensureArray(filters.category_id).map(Number)
      }
      if (!parsedSearch.language_id && filters.coupon_lang) {
        parsedSearch.language_id = ensureArray(filters.coupon_lang).map(Number)
      }
      if (!parsedSearch.offers && filters.coupon_offer) {
        parsedSearch.offers = ensureArray(filters.coupon_offer).map(Number)
      }
      if (!parsedSearch.status && filters.coupon_status) {
        parsedSearch.status = ensureArray(filters.coupon_status).map(Number)
      }
      if (!parsedSearch.is_exclusive && filters.is_exclusive) {
        parsedSearch.is_exclusive = filters.is_exclusive?.[0] === 'true'
      }
      if (!parsedSearch.is_personal && filters.is_personal) {
        parsedSearch.is_personal = filters.is_personal?.[0] === 'true'
      }
      if (!parsedSearch.type && filters.promo_type) {
        parsedSearch.type = ensureArray(filters.promo_type).map(Number)
      }
      if (!parsedSearch.region_id && filters.region_id) {
        parsedSearch.region_id = ensureArray(filters.region_id).map(Number)
      }
      if (!parsedSearch.scopes && filters.scopes) {
        parsedSearch.scopes = ensureArray(filters.scopes).map(Number)
      }
      if (!parsedSearch.sectoring && filters.sectoring) {
        parsedSearch.sectoring = ensureArray(filters.sectoring).map(Number)
      }

    } catch (e) { }
    delete (parsedSearch as LocationParams & { filter: any }).filter;
  }

  return parsedSearch;
}

function useSetLocation() {
  const location = useLocation()
  const history = useHistory()
  const parseLocation = useParseLocation()

  return useCallback((params: Partial<LocationParams>) => {
    const prevData = parseLocation(location.search);
    const nextData = {
      ...prevData,
      ...params,
    }

    if (!nextData.is_exclusive) { delete nextData.is_exclusive }
    if (!nextData.is_personal) { delete nextData.is_personal }

    const search = '?' + queryString.stringify(nextData, {
      arrayFormat: 'comma',
    })

    if (location.search !== search) {
      history.push({
        search
      })
    }

  }, [
    location,
    history,
    parseLocation
  ])
}

/**
 * Сброс всех фильтров
 */
export function useClearFilters(form: ReturnType<typeof useSelectedFilters>) {
  return useCallback(() => {
    form.setFormData({
      category: [],
      is_exclusive: false,
      is_personal: false,
      language_id: [],
      offers: [],
      region_id: [],
      scopes: [],
      sectoring: [],
      status: [],
      type: [],
    }, true);
    form.handleSubmit();
  }, [form])
}


/**
 *
 */
export function usePaginatorActions() {
  const setLocation = useSetLocation()

  const setPage = useCallback((page: number) => {
    setLocation({ page })
  }, [setLocation])

  const setPageLimit = useCallback((limit: number) => {
    setLocation({ page: 1, limit })
  }, [setLocation])


  return {
    setPage,
    setPageLimit
  }
}


/**
 * Справочники для фильтров
 */
export function useEntityLists() {
  const regionList = useEntities<RegionTreeItem[]>('regionTree', null, true);
  const categoryList = useEntities<CategoryTreeItem[]>('couponCategoryTree', null, true);
  const offersById = useSelector((state: RootState) => state.offers.couponOffersById);
  const offers = useSelector((state: RootState) => state.offers.coupon_offer);

  const couponOfferList = offers
    .map(id => {
      const offer = offersById[id];
      return { title: offer.translated_name ?? offer.name, value: offer.id }
    }) // TODO: перенести в entities

  const couponTypeList = useSelector((state: RootState) => state.offers.promo_type) // TODO: перенести в entities
  const couponStatusList = useSelector((state: RootState) => state.offers.coupon_status) // TODO: перенести в entities
  const couponLanguageList = useSelector((state: RootState) => state.language.sadukaLanguages)
  const couponScopeList = useSelector((state: RootState) => state.offers.couponScopes)
  const couponSectoringList = useSelector((state: RootState) => state.offers.sectoring)

  return {
    regionList,
    categoryList,
    couponOfferList,
    couponTypeList,
    couponStatusList,
    couponLanguageList,
    couponScopeList,
    couponSectoringList,
  }
}


/**
 * Загрузка количества найденных
 * результатов по фильтру
 */
function useLoadTotal(
  selectedFilters: ReturnType<typeof useSelectedFilters>
) {
  const dispatch = useDispatch()

  const loadTotal = useCallback(() => {
    const data = selectedFilters.getFormData()
    if (!data.is_exclusive) delete data.is_exclusive;
    if (!data.is_personal) delete data.is_personal;

    dispatch(getFilterCount({
      isTypeCoupon: true,
      filter: {
        ...pick(data, [
          'category',
          'is_exclusive',
          'is_personal',
          'language_id',
          'sectoring',
          'offers',
          'region_id',
          'scopes',
          'status',
          'type',
        ])
      }
    }))

  }, [
    dispatch,
    selectedFilters.getFormData,
    selectedFilters.fields.category.value,
    selectedFilters.fields.is_exclusive.value,
    selectedFilters.fields.is_personal.value,
    selectedFilters.fields.language_id.value,
    selectedFilters.fields.sectoring.value,
    selectedFilters.fields.offers.value,
    selectedFilters.fields.region_id.value,
    selectedFilters.fields.scopes.value,
    selectedFilters.fields.status.value,
    selectedFilters.fields.type.value,
  ])

  const load = useDebounceAction(loadTotal, config.KEYBOARD_TIMEOUT)

  useEffect(() => {
    load()
  }, [
    load
  ])
}
